import PropTypes from 'prop-types';
import { reaction, extendObservable } from 'mobx';
import { apiCall } from '../../../API';
import ConcurrencyPopUpStore from '../../ConcurrencyPopUpStore';
import CategoriesSectionStore from '../CategoriesSectionStore';
import PolicyTableStore from '../PolicyTableStore';
import MatchCriteriaStore from '../MatchCriteriaStore';
import FileActionsStore from './FileActionsStore';
import FileTypesStore from './FileTypesStore';
import SourceCountriesStore from './SourceCountriesStore';
import FileRuleActionStore from './FileRuleActionStore';
import AdditionalConditionsStore from './AdditionalConditionsStore';
import FileSizeStore from './FileSizeStore';
import { RULE_REC_TYPE } from '../../../constants';
import FlagStore from '../../FlagStore';

export default class FileRulesStore {
    static PropType = PropTypes.shape({
        policyTable: PolicyTableStore.PropType.isRequired,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class FileRulesStore */{
            policyTable: new PolicyTableStore({
                apiList: apiCall(`${data.path}/list`),
                apiChangePriorities: apiCall(`${data.path}/rearrange`),
                modalSections: {
                    matchCriteria: new MatchCriteriaStore({
                        extraSections: {
                            fileActions: new FileActionsStore(),
                            fileSize: new FileSizeStore(),
                            fileTypes: new FileTypesStore({
                                apiGetFileTypes: apiCall('/console/mime-types'),
                                supportedFileTypes: data.supportedFileTypes
                            }),
                            sourceCountries: new SourceCountriesStore()
                        },
                    }),
                    action: new FileRuleActionStore(),
                    categories: new CategoriesSectionStore({
                        externalValidationFn: () => true
                    }),
                    additionalConditions: new AdditionalConditionsStore({
                        apiGetDetectorSets: apiCall('/console/platformx/detectorsets'),
                        shouldSkipValidation: new FlagStore(true),
                        ruleType: data.type,
                    })
                },
                concurrencyPopUpStore: new ConcurrencyPopUpStore({
                    apiVersionedUpdate: apiCall(`${data.path}/versioned/update`),
                    apiUpdate: apiCall(`${data.path}/update`),
                    apiCreate: apiCall(`${data.path}/create`),
                    apiVersionedDelete: apiCall(`${data.path}/versioned/delete`),
                    apiDelete: ({ id }) => apiCall(`${data.path}/delete/${id}`)(),
                    type: data.type
                }),
                onLoadListeners: [(items) => {
                    items.forEach((rule) => {
                        const hiddenDefaults = rule.frontendOptions ? JSON.parse(rule.frontendOptions) : [];
                        hiddenDefaults.forEach((item) => {
                            rule[item] = item === 'channels' ? [] : false;
                        });
                    });
                }],
                preprocessBeforeSubmit: (value) => {
                    const frontendOptions = [];
                    if (!value.matchAllFileTypes && value.matchFileTypes.items.length === 0) {
                        value.matchAllFileTypes = true;
                        frontendOptions.push('matchAllFileTypes');
                    }
                    if (!value.matchAllFileSize && !value.maximumSizeAllowed) {
                        value.matchAllFileSize = true;
                        frontendOptions.push('matchAllFileSize');
                    }
                    if (!value.matchAllCountries && value.matchCountries.items.length === 0) {
                        value.matchAllCountries = true;
                        frontendOptions.push('matchAllCountries');
                    }
                    if (!value.matchAllCategories
                        && value.customerCategories.length === 0
                        && value.standardCategories.length === 0) {
                        value.matchAllCategories = true;
                        frontendOptions.push('matchAllCategories');
                    }

                    if (frontendOptions.length > 0) {
                        value.frontendOptions = JSON.stringify(frontendOptions);
                    } else {
                        delete value.frontendOptions;
                    }
                },
            }),
            type: data.type,
        }, data);
        reaction(() => this.policyTable.policyModal.sections.action.action.value, (value) => {
            if (this.type === RULE_REC_TYPE.DLP_RULES) {
                this.policyTable.policyModal.sections
                    .additionalConditions.shouldSkipValidation.setValue(value !== 'SCAN');
            }
        });
    }
}
